import {FirebaseOptions, getApp, getApps, initializeApp} from 'firebase/app';
import {deleteToken, getMessaging, getToken, isSupported, Messaging} from 'firebase/messaging';
import createAxiosInstance from './AxiosInstance';

const PUSH_TOKEN_STORAGE_KEY = 'push-notification-token';
const PUSH_TOKEN_ENDPOINT = '/user/account-settings/push-notifications/token';

export type PushNotificationsStatus = {
    isSupported: boolean;
    hasFirebaseConfig: boolean;
    permission: NotificationPermission | 'unsupported';
    hasStoredToken: boolean;
};

const getFirebaseConfig = (): FirebaseOptions | null => {
    const {
        REACT_APP_FIREBASE_API_KEY,
        REACT_APP_FIREBASE_AUTH_DOMAIN,
        REACT_APP_FIREBASE_PROJECT_ID,
        REACT_APP_FIREBASE_STORAGE_BUCKET,
        REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        REACT_APP_FIREBASE_APP_ID,
    } = process.env;

    if (
        !REACT_APP_FIREBASE_API_KEY ||
        !REACT_APP_FIREBASE_AUTH_DOMAIN ||
        !REACT_APP_FIREBASE_PROJECT_ID ||
        !REACT_APP_FIREBASE_STORAGE_BUCKET ||
        !REACT_APP_FIREBASE_MESSAGING_SENDER_ID ||
        !REACT_APP_FIREBASE_APP_ID
    ) {
        return null;
    }

    return {
        apiKey: REACT_APP_FIREBASE_API_KEY,
        authDomain: REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: REACT_APP_FIREBASE_APP_ID,
    };
};

const hasFirebaseConfig = (): boolean => {
    return Boolean(getFirebaseConfig() && process.env.REACT_APP_FIREBASE_VAPID_KEY);
};

const setStoredToken = (token: string | null): void => {
    if (token) {
        localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);
        return;
    }

    localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
};

const getStoredToken = (): string | null => {
    return localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
};

const ensureMessagingSupport = async (): Promise<void> => {
    if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error('unsupported');
    }

    if (!(await isSupported())) {
        throw new Error('unsupported');
    }
};

const getMessagingInstance = async (): Promise<Messaging> => {
    await ensureMessagingSupport();

    const firebaseConfig = getFirebaseConfig();
    if (!firebaseConfig || !process.env.REACT_APP_FIREBASE_VAPID_KEY) {
        throw new Error('missing-config');
    }

    const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    return getMessaging(app);
};

const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration> => {
    if (process.env.NODE_ENV !== 'production') {
        throw new Error('push-production-only');
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
        throw new Error('missing-service-worker');
    }

    return registration;
};

const getCurrentToken = async (): Promise<string> => {
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    const messaging = await getMessagingInstance();
    const serviceWorkerRegistration = await getServiceWorkerRegistration();

    const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration,
    });

    if (!token) {
        throw new Error('missing-token');
    }

    return token;
};

const resolveKnownToken = async (): Promise<string | null> => {
    const storedToken = getStoredToken();
    if (storedToken) {
        return storedToken;
    }

    const status = await getPushNotificationsStatus();
    if (!status.isSupported || !status.hasFirebaseConfig || status.permission !== 'granted') {
        return null;
    }

    try {
        return await getCurrentToken();
    } catch {
        return null;
    }
};

const syncTokenWithBackend = async (token: string): Promise<void> => {
    const axios = createAxiosInstance();
    await axios.post(PUSH_TOKEN_ENDPOINT, {
        token,
        platform: 'web',
        userAgent: navigator.userAgent,
    });
};

const removeTokenFromBackend = async (token: string): Promise<void> => {
    const axios = createAxiosInstance();
    await axios.delete(PUSH_TOKEN_ENDPOINT, {
        data: {
            token,
            platform: 'web',
        },
    });
};

export const getPushNotificationsStatus = async (): Promise<PushNotificationsStatus> => {
    const permission = typeof window !== 'undefined' && 'Notification' in window
        ? Notification.permission
        : 'unsupported';

    try {
        await ensureMessagingSupport();
        return {
            isSupported: true,
            hasFirebaseConfig: hasFirebaseConfig(),
            permission,
            hasStoredToken: Boolean(getStoredToken()),
        };
    } catch {
        return {
            isSupported: false,
            hasFirebaseConfig: hasFirebaseConfig(),
            permission: 'unsupported',
            hasStoredToken: Boolean(getStoredToken()),
        };
    }
};

export const registerPushNotifications = async (): Promise<PushNotificationsStatus> => {
    const status = await getPushNotificationsStatus();
    if (!status.isSupported) {
        throw new Error('unsupported');
    }
    if (!status.hasFirebaseConfig) {
        throw new Error('missing-config');
    }

    const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();

    if (permission !== 'granted') {
        throw new Error(permission === 'denied' ? 'permission-denied' : 'permission-dismissed');
    }

    const token = await getCurrentToken();
    await syncTokenWithBackend(token);
    setStoredToken(token);

    return getPushNotificationsStatus();
};

export const syncPushNotificationsIfEnabled = async (): Promise<void> => {
    const status = await getPushNotificationsStatus();
    if (!status.isSupported || !status.hasFirebaseConfig || status.permission !== 'granted') {
        return;
    }

    try {
        const token = await getCurrentToken();
        await syncTokenWithBackend(token);
        setStoredToken(token);
    } catch (error) {
        console.warn('[Push] Skipping token sync.', error);
    }
};

export const unlinkPushNotificationsForCurrentUser = async (): Promise<void> => {
    const token = await resolveKnownToken();
    if (!token) {
        return;
    }

    try {
        await removeTokenFromBackend(token);
    } finally {
        setStoredToken(null);
    }
};

export const disablePushNotifications = async (): Promise<PushNotificationsStatus> => {
    const status = await getPushNotificationsStatus();
    if (!status.isSupported || !status.hasFirebaseConfig) {
        throw new Error('unsupported');
    }

    const token = await resolveKnownToken();
    if (token) {
        await removeTokenFromBackend(token);
    }

    const messaging = await getMessagingInstance();
    await deleteToken(messaging);
    setStoredToken(null);

    return getPushNotificationsStatus();
};